/* V165.1 — Facebook Pixel + CAPI dual-channel tracker
 *
 * Single API: trackEvent("Lead", { value, currency, email, ... })
 *   1. Generates one UUID event_id
 *   2. Calls fbq("track", ...) with eventID for browser-side Pixel
 *   3. POSTs same payload to /api/meta/conversion for server-side CAPI
 *      → Meta dedupes via event_id
 *
 * Privacy: never sends raw email/phone to fbq — those go only to CAPI which
 * SHA256-hashes them server-side.
 */
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

let PIXEL_READY = false;
let PIXEL_ID = null;
let PIXEL_BOOT_PROMISE = null;

function getCookie(name) {
  if (typeof document === "undefined") return undefined;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : undefined;
}

function injectPixelSnippet(pixelId) {
  /* eslint-disable */
  if (typeof window === "undefined") return;
  if (window.fbq) return;
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", pixelId);
  /* eslint-enable */
}

/** Boot the Pixel ONCE on first call. Reads config from /api/meta/config so
 *  the FE doesn't ship the Pixel id at build time — only loaded if owner
 *  has set FACEBOOK_PIXEL_ID in backend .env. */
export async function ensurePixelReady() {
  if (PIXEL_READY) return true;
  if (PIXEL_BOOT_PROMISE) return PIXEL_BOOT_PROMISE;
  PIXEL_BOOT_PROMISE = (async () => {
    try {
      const r = await axios.get(`${API}/meta/config`);
      if (r.data?.pixel_enabled && r.data?.pixel_id) {
        PIXEL_ID = r.data.pixel_id;
        injectPixelSnippet(PIXEL_ID);
        PIXEL_READY = true;
        return true;
      }
    } catch {
      /* config endpoint failure → silently disable */
    }
    return false;
  })();
  return PIXEL_BOOT_PROMISE;
}

function genEventId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback: timestamp + random
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

/** Track a standard Meta event in both Pixel + CAPI with dedup.
 *  @param eventName one of: PageView, Lead, CompleteRegistration, Purchase, ViewContent, AddToCart
 *  @param payload   { value?, currency?, email?, phone?, ...customData }
 */
export async function trackEvent(eventName, payload = {}) {
  const eventId = genEventId();
  const eventTime = Math.floor(Date.now() / 1000);

  await ensurePixelReady().catch(() => false);

  // 1) Browser Pixel — only non-PII params
  try {
    if (window.fbq) {
      const pixelParams = {};
      if (payload.value != null) pixelParams.value = payload.value;
      if (payload.currency) pixelParams.currency = payload.currency;
      if (payload.content_name) pixelParams.content_name = payload.content_name;
      if (payload.content_category) pixelParams.content_category = payload.content_category;
      window.fbq("track", eventName, pixelParams, { eventID: eventId });
    }
  } catch {
    /* fbq is optional */
  }

  // 2) Server-side CAPI mirror — includes PII for hashing
  try {
    await axios.post(`${API}/meta/conversion`, {
      event_name: eventName,
      event_id: eventId,
      event_time: eventTime,
      event_source_url: typeof window !== "undefined" ? window.location.href : null,
      value: payload.value ?? null,
      currency: payload.currency ?? null,
      user_email: payload.email ?? null,
      user_phone: payload.phone ?? null,
      fbp: getCookie("_fbp") || null,
      fbc: getCookie("_fbc") || null,
      custom_data: payload.custom_data || {},
    });
  } catch {
    /* network failures shouldn't break UX */
  }

  return eventId;
}

/** React hook helper: track PageView on every route change.
 *  Usage in App: useTrackPageView() inside a component that mounts inside <Router>.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export function useTrackPageView() {
  const location = useLocation();
  useEffect(() => {
    trackEvent("PageView", {});
  }, [location.pathname]);
}
